import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DRXError } from '@/lib/drx-error';
import type {
  ITenantRepository,
  CreateTenantData,
  UpdateTenantData,
  GetTenantData,
} from './interfaces/tenant.repository.interface';

export class TenantRepository implements ITenantRepository {
  private collectionName = 'tenants';

  async createTenant(tenantData: CreateTenantData): Promise<GetTenantData> {
    try {
      const colRef = collection(db, this.collectionName);
      const docRef = await addDoc(colRef, {
        ...tenantData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return {
        id: docRef.id,
        ...tenantData,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as GetTenantData;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'TENANT_CREATE_FAILED',
        devMessage: `Failed to create tenant "${tenantData.name}"`,
        cause: error,
        level: 'error',
      });
    }
  }

  async getTenants(): Promise<GetTenantData[]> {
    try {
      const colRef = collection(db, this.collectionName);
      const snapshot = await getDocs(colRef);
      return snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as unknown as GetTenantData[];
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'TENANT_GET_ALL_FAILED',
        devMessage: 'Failed to get tenants',
        cause: error,
        level: 'error',
      });
    }
  }

  async updateTenantSettings(tenantId: string, settings: UpdateTenantData): Promise<GetTenantData> {
    try {
      const docRef = doc(db, this.collectionName, tenantId);
      await updateDoc(docRef, {
        settings,
        updatedAt: serverTimestamp(),
      });

      const updated = await getDoc(docRef);
      return {
        id: tenantId,
        ...updated.data(),
      } as unknown as GetTenantData;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'TENANT_UPDATE_SETTINGS_FAILED',
        devMessage: `Failed to update settings for tenant ${tenantId}`,
        cause: error,
        level: 'error',
      });
    }
  }
}
